import { Eye, EyeOff, Trophy, type LucideIcon } from 'lucide-react';
import { useState, type InputHTMLAttributes, type ReactNode } from 'react';

/** Shared visual shell for the public auth pages (Login, Invite/Registration)
 * — a centered card on a soft gradient backdrop, branded with the club's own
 * logo when one is known (e.g. from an invite preview), falling back to a
 * generic mark otherwise (login has no org context yet).
 *
 * `name` is the big, standalone headline — the club's name on the
 * registration page, "Badminton Manager" on login (there's no specific club
 * yet at that point) — so it's always clear at a glance *where* one is
 * signing in/up, before even reading the smaller `title` line underneath. */
export function AuthCard({
  name,
  title,
  subtitle,
  logoUrl,
  children,
  footer,
}: {
  name: string;
  title: string;
  subtitle?: string;
  logoUrl?: string | null;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-1 text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              className="mb-2 h-14 w-14 rounded-2xl border border-white bg-white object-contain shadow-md"
            />
          ) : (
            <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-accent to-indigo-600 text-white shadow-lg shadow-brand-accent/20">
              <Trophy size={26} />
            </div>
          )}
          <h1 className="text-2xl font-bold text-slate-900">{name}</h1>
          <p className="text-base font-medium text-slate-500">{title}</p>
          {subtitle && <p className="mt-1 max-w-xs text-sm text-slate-400">{subtitle}</p>}
        </div>

        <div className="rounded-2xl border border-white/60 bg-white/90 p-6 shadow-xl shadow-slate-900/5 backdrop-blur-sm sm:p-8">
          {children}
        </div>

        {footer && <div className="mt-6 text-center text-sm text-slate-500">{footer}</div>}
      </div>
    </div>
  );
}

/** Text/email/password input with a leading icon and, for passwords, a
 * show/hide toggle — the small details that make a form feel modern instead
 * of a bare `<input>`. */
export function AuthInput({
  icon: Icon,
  type = 'text',
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { icon: LucideIcon }) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div className="relative">
      <Icon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        type={isPassword ? (show ? 'text' : 'password') : type}
        className={`w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 text-sm outline-none transition focus:border-brand-accent focus:bg-white focus:ring-2 focus:ring-brand-accent/20 ${isPassword ? 'pr-9' : 'pr-3'} ${className}`}
        {...props}
      />
      {isPassword && (
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      )}
    </div>
  );
}

export function AuthLabel({ children }: { children: ReactNode }) {
  return <label className="mb-1 block text-sm font-medium text-slate-600">{children}</label>;
}
